import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import Header from '../common/Header';
import Sidebar from '../common/Sidebar';
import Spinner from '../common/Spinner';
import CreateEvent from './CreateEvent';
import MarkAttendance from './MarkAttendance';
import InviteModal from './InviteModal';
import api, { getStaticBaseUrl } from '../../services/api';
import toast from 'react-hot-toast';
import { FaPlus, FaEye, FaCheckCircle, FaUsers, FaLink, FaTimes, FaEdit, FaSearch, FaUserTie, FaFileAlt, FaTrash, FaPlayCircle, FaFilePdf, FaCalendarCheck } from 'react-icons/fa';
import { getDrafts, deleteDraft } from '../../utils/eventDrafts';
import { hasEventStarted, getEventStartDateTime, formatIST } from '../../utils/eventTiming';

const StaffDashboard = () => {
    const { user } = useAuth();
    const [activeSection, setActiveSection] = useState('events');
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [drafts, setDrafts] = useState([]);
    const [draftToEdit, setDraftToEdit] = useState(null);
    const [showAttendance, setShowAttendance] = useState(false);
    const [busyEventId, setBusyEventId] = useState(null); // per-event button loading (View Students / Edit fetch)
    const [showStudentsList, setShowStudentsList] = useState(false);
    const [selectedEventStudents, setSelectedEventStudents] = useState([]);
    const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
    const [showStudentDetails, setShowStudentDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isMobile, setIsMobile] = useState(false);
    const [showPosterPopup, setShowPosterPopup] = useState(false);
    const [selectedPoster, setSelectedPoster] = useState('');
    
    // Invite Modal States
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [inviteEvent, setInviteEvent] = useState(null);

    // Edit Event States
    const [showEditEvent, setShowEditEvent] = useState(false);
    const [editEventId, setEditEventId] = useState(null);
    const [editCreatedBy, setEditCreatedBy] = useState(null);
    const [editFormData, setEditFormData] = useState({ name: '', synopsis: '', date: '', time: '', venue: '', poster: null });
    const [editStudents, setEditStudents] = useState([]);
    const [editCoordinators, setEditCoordinators] = useState([]);
    const [allStudents, setAllStudents] = useState([]);
    const [allStaff, setAllStaff] = useState([]);
    const [editStudentSearch, setEditStudentSearch] = useState('');
    const [showEditStudentDropdown, setShowEditStudentDropdown] = useState(false);
    const [editCoordinatorSearch, setEditCoordinatorSearch] = useState('');
    const [showEditCoordinatorDropdown, setShowEditCoordinatorDropdown] = useState(false);
    const [editSaving, setEditSaving] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        refreshDrafts();
    }, [user?._id]);

    const refreshDrafts = () => {
        setDrafts(getDrafts(user?._id));
    };

    const handleResumeDraft = (draft) => {
        setDraftToEdit(draft);
        setActiveSection('create');
        setShowAttendance(false);
    };

    const handleDeleteDraft = (draftId) => {
        deleteDraft(user?._id, draftId);
        refreshDrafts();
        toast.success('🗑️ Draft deleted');
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        try {
            const response = await api.get('/events');
            setEvents(response.data.events || []);
        } catch (error) {
            toast.error('Failed to fetch events');
        } finally {
            setLoading(false);
        }
    };

    const handleViewEvent = async (eventId) => {
        setBusyEventId(eventId);
        try {
            const response = await api.get(`/events/${eventId}`);
            const event = response.data.event;
            const attendance = event.attendance || [];

            const studentsWithAttendance = event.students.map(student => {
                const att = attendance.find(a => a.student._id === student._id);
                return {
                    ...student,
                    attendanceStatus: att ? att.status : 'not_marked'
                };
            });

            setSelectedEventStudents(studentsWithAttendance);
            setShowStudentsList(true);
            toast.success(`Loaded ${studentsWithAttendance.length} students`);
        } catch (error) {
            toast.error('Failed to fetch event details');
        } finally {
            setBusyEventId(null);
        }
    };

    const handleViewStudentDetails = (student) => {
        setSelectedStudentDetails(student);
        setShowStudentDetails(true);
    };

    const handleViewPoster = (posterUrl) => {
        setSelectedPoster(posterUrl);
        setShowPosterPopup(true);
    };

    const handleMarkAttendance = (event) => {
        setSelectedEvent(event);
        setShowAttendance(true);
    };

    const openInviteModal = (event) => {
        setInviteEvent(event);
        setShowInviteModal(true);
    };

    const toDateInputValue = (dateStr) => {
        const d = new Date(dateStr);
        return isNaN(d) ? '' : d.toISOString().split('T')[0];
    };

    const openEditEvent = async (event) => {
        setBusyEventId(event._id);
        try {
            const response = await api.get(`/events/${event._id}`);
            const full = response.data.event;
            setEditEventId(full._id);
            setEditCreatedBy(full.createdBy?._id);
            setEditFormData({
                name: full.name || '',
                synopsis: full.synopsis || '',
                date: toDateInputValue(full.date),
                time: full.time || '',
                venue: full.venue || '',
                poster: null
            });
            setEditStudents(full.students || []);
            setEditCoordinators((full.coordinators || []).filter(c => c._id !== full.createdBy?._id));

            if (allStudents.length === 0) {
                const studentsRes = await api.get('/staff/students');
                setAllStudents(studentsRes.data.users || []);
            }
            if (allStaff.length === 0) {
                const staffRes = await api.get('/admin/staff-list');
                setAllStaff(staffRes.data.users || []);
            }

            setShowEditEvent(true);
        } catch (error) {
            toast.error('Failed to load event for editing');
        } finally {
            setBusyEventId(null);
        }
    };

    const addEditStudent = (student) => {
        if (!editStudents.find(s => s._id === student._id)) {
            setEditStudents([...editStudents, student]);
        }
        setEditStudentSearch('');
        setShowEditStudentDropdown(false);
    };

    const removeEditStudent = (id) => {
        setEditStudents(editStudents.filter(s => s._id !== id));
    };

    const addEditCoordinator = (staff) => {
        if (editCoordinators.length >= 19) {
            toast.error('Maximum 19 secondary coordinators allowed (plus creator = 20 total)');
            return;
        }
        if (!editCoordinators.find(c => c._id === staff._id) && staff._id !== editCreatedBy) {
            setEditCoordinators([...editCoordinators, staff]);
        }
        setEditCoordinatorSearch('');
        setShowEditCoordinatorDropdown(false);
    };

    const removeEditCoordinator = (id) => {
        setEditCoordinators(editCoordinators.filter(c => c._id !== id));
    };

    const filteredEditStudents = allStudents.filter(s =>
        editStudentSearch &&
        (s.name?.toLowerCase().includes(editStudentSearch.toLowerCase()) ||
         s.regNo?.toLowerCase().includes(editStudentSearch.toLowerCase()))
    );

    const filteredEditCoordinators = allStaff.filter(s =>
        s._id !== editCreatedBy &&
        (editCoordinatorSearch
            ? (s.name?.toLowerCase().includes(editCoordinatorSearch.toLowerCase()) ||
               s.email?.toLowerCase().includes(editCoordinatorSearch.toLowerCase()))
            : true)
    );

    const handleUpdateEvent = async (e) => {
        e.preventDefault();
        if (!editFormData.name || !editFormData.date || !editFormData.time) {
            toast.error('Please fill in all required fields');
            return;
        }

        setEditSaving(true);
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', editFormData.name);
            formDataToSend.append('synopsis', editFormData.synopsis || '');
            formDataToSend.append('date', editFormData.date);
            formDataToSend.append('time', editFormData.time);
            formDataToSend.append('venue', editFormData.venue || '');
            formDataToSend.append('students', JSON.stringify(editStudents.map(s => s._id)));
            formDataToSend.append('coordinators', JSON.stringify(editCoordinators.map(c => c._id)));
            if (editFormData.poster) {
                formDataToSend.append('poster', editFormData.poster);
            }

            await api.put(`/events/${editEventId}`, formDataToSend, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            toast.success('✅ Event updated successfully!');
            setShowEditEvent(false);
            fetchEvents();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update event');
        } finally {
            setEditSaving(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}>Loading...</div>;
    }

    const sidebarItems = [
        { key: 'events', label: 'My Events', icon: <FaCalendarCheck /> },
        { key: 'create', label: 'Create Event', icon: <FaPlus /> },
        { key: 'drafts', label: `Drafts (${drafts.length})`, icon: <FaFileAlt /> }
    ];

    const handleSidebarSelect = (key) => {
        setActiveSection(key);
        setShowAttendance(false);
        if (key === 'create') setDraftToEdit(null);
    };

    return (
        <div>
            <Header title="Staff Dashboard" />
            <div className="dashboard-layout">
                <Sidebar items={sidebarItems} activeKey={activeSection} onSelect={handleSidebarSelect} />
                <div className="dashboard-content">
                {showAttendance && selectedEvent && (
                    <MarkAttendance
                        event={selectedEvent}
                        user={user}
                        onComplete={() => {
                            setShowAttendance(false);
                            fetchEvents();
                        }}
                    />
                )}

                {showInviteModal && inviteEvent && (
                    <InviteModal 
                        event={inviteEvent} 
                        onClose={() => setShowInviteModal(false)}
                        onSuccess={() => {
                            toast.success('Invitation sent successfully!');
                            fetchEvents();
                        }}
                    />
                )}

                {/* Students List Modal */}
                {showStudentsList && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowStudentsList(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            maxWidth: '900px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: 'var(--srm-blue)' }}>
                                    <FaUsers /> Students List ({selectedEventStudents.length})
                                </h3>
                                <button onClick={() => setShowStudentsList(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div className="table-container">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Reg No</th>
                                            <th>Program</th>
                                            <th>Branch</th>
                                            <th>Year</th>
                                            <th>Section</th>
                                            <th>Attendance</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedEventStudents.map((s, i) => (
                                            <tr key={i}>
                                                <td>{s.name}</td>
                                                <td>{s.regNo || 'N/A'}</td>
                                                <td>{s.program || 'N/A'}</td>
                                                <td>{s.branch || 'N/A'}</td>
                                                <td>{s.year || 'N/A'}</td>
                                                <td>{s.section || 'N/A'}</td>
                                                <td>
                                                    <span className={`badge ${s.attendanceStatus === 'present' ? 'badge-success' : s.attendanceStatus === 'absent' ? 'badge-danger' : 'badge-warning'}`}>
                                                        {s.attendanceStatus === 'present' ? 'Present' : s.attendanceStatus === 'absent' ? 'Absent' : 'Not Marked'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button className="btn btn-sm btn-primary" onClick={() => handleViewStudentDetails(s)}>
                                                        <FaEye /> View
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowStudentsList(false)}>Close</button>
                        </div>
                    </div>
                )}

                {/* Student Details Modal */}
                {showStudentDetails && selectedStudentDetails && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowStudentDetails(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '25px',
                            maxWidth: '550px',
                            width: '100%',
                            maxHeight: '80vh',
                            overflow: 'auto'
                        }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ color: 'var(--srm-blue)' }}>Student Details</h3>
                                <button onClick={() => setShowStudentDetails(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                <div><strong>Name:</strong> {selectedStudentDetails.name}</div>
                                <div><strong>Email:</strong> {selectedStudentDetails.email}</div>
                                <div><strong>Reg No:</strong> {selectedStudentDetails.regNo || 'N/A'}</div>
                                <div><strong>Program:</strong> {selectedStudentDetails.program || 'N/A'}</div>
                                <div><strong>Branch:</strong> {selectedStudentDetails.branch || 'N/A'}</div>
                                <div><strong>Year:</strong> {selectedStudentDetails.year || 'N/A'}</div>
                                <div><strong>Section:</strong> {selectedStudentDetails.section || 'N/A'}</div>
                                <div><strong>Contact:</strong> {selectedStudentDetails.contact || 'N/A'}</div>
                                <div><strong>Parent Name:</strong> {selectedStudentDetails.parentName || 'N/A'}</div>
                                <div><strong>Parent Contact:</strong> {selectedStudentDetails.parentContact || 'N/A'}</div>
                                <div><strong>Parent Email:</strong> {selectedStudentDetails.parentEmail || 'N/A'}</div>
                                <div><strong>Status:</strong> 
                                    <span className={`badge ${selectedStudentDetails.status === 'active' ? 'badge-success' : 'badge-danger'}`}>
                                        {selectedStudentDetails.status || 'N/A'}
                                    </span>
                                </div>
                            </div>
                            <button className="btn btn-primary" style={{ marginTop: '15px' }} onClick={() => setShowStudentDetails(false)}>Close</button>
                        </div>
                    </div>
                )}

                {/* Poster Popup */}
                {showPosterPopup && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.85)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        padding: '20px'
                    }} onClick={() => setShowPosterPopup(false)}>
                        <div style={{
                            backgroundColor: 'white',
                            borderRadius: '12px',
                            padding: '20px',
                            maxWidth: '90%',
                            maxHeight: '90%',
                            position: 'relative'
                        }} onClick={(e) => e.stopPropagation()}>
                            <button 
                                onClick={() => setShowPosterPopup(false)}
                                style={{
                                    position: 'absolute',
                                    top: '-15px',
                                    right: '-15px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    fontSize: '20px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 100
                                }}
                            >
                                <FaTimes />
                            </button>
                            <img
                                src={`${getStaticBaseUrl()}/${selectedPoster}`}
                                alt="Event Poster" 
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '80vh',
                                    borderRadius: '8px'
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Edit Event Modal */}
                {showEditEvent && (
                    <div style={{
                        position: 'fixed',
                        top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px'
                    }} onClick={() => setShowEditEvent(false)}>
                        <div className="card" style={{ maxWidth: '700px', width: '100%', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 className="card-title" style={{ marginBottom: 0 }}>Edit Event</h3>
                                <button onClick={() => setShowEditEvent(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>
                                    <FaTimes />
                                </button>
                            </div>
                            <form onSubmit={handleUpdateEvent}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                    <div className="form-group">
                                        <label>Event Name *</label>
                                        <input className="form-control" required value={editFormData.name} onChange={e => setEditFormData({...editFormData, name: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Venue</label>
                                        <input className="form-control" value={editFormData.venue} onChange={e => setEditFormData({...editFormData, venue: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Date *</label>
                                        <input type="date" className="form-control" required value={editFormData.date} onChange={e => setEditFormData({...editFormData, date: e.target.value})} />
                                    </div>
                                    <div className="form-group">
                                        <label>Time *</label>
                                        <input type="time" className="form-control" required value={editFormData.time} onChange={e => setEditFormData({...editFormData, time: e.target.value})} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label>Event Synopsis</label>
                                    <textarea className="form-control" rows="3" value={editFormData.synopsis} onChange={e => setEditFormData({...editFormData, synopsis: e.target.value})} />
                                </div>

                                <div className="form-group">
                                    <label>Replace Poster (optional)</label>
                                    <input type="file" className="form-control" accept="image/*" onChange={e => setEditFormData({...editFormData, poster: e.target.files[0]})} />
                                    {editFormData.poster && <p style={{ fontSize: '12px', color: 'green', marginTop: '5px' }}>✅ Selected: {editFormData.poster.name}</p>}
                                </div>

                                {/* Students */}
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <label>Students ({editStudents.length})</label>
                                    <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--srm-border)', borderRadius: '8px', background: 'white' }}>
                                        <FaSearch style={{ marginLeft: '10px', color: '#999' }} />
                                        <input type="text" className="form-control" style={{ border: 'none', background: 'transparent' }}
                                            value={editStudentSearch}
                                            onChange={(e) => { setEditStudentSearch(e.target.value); setShowEditStudentDropdown(true); }}
                                            onFocus={() => setShowEditStudentDropdown(true)}
                                            placeholder="Search to add a student..." />
                                    </div>
                                    {showEditStudentDropdown && editStudentSearch && filteredEditStudents.length > 0 && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--srm-border)', borderRadius: '8px', maxHeight: '180px', overflowY: 'auto', zIndex: 1000, marginTop: '5px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                            {filteredEditStudents.map(s => (
                                                <div key={s._id} onClick={() => addEditStudent(s)} style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                                                    <strong>{s.name}</strong> <span style={{ color: '#666', marginLeft: '10px' }}>{s.regNo || 'N/A'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {editStudents.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '10px', background: 'var(--srm-gray)', borderRadius: '8px', marginTop: '8px' }}>
                                            {editStudents.map(s => (
                                                <span key={s._id} style={{ background: 'var(--srm-blue)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    {s.name} ({s.regNo || 'N/A'})
                                                    <span onClick={() => removeEditStudent(s._id)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Coordinators */}
                                <div className="form-group" style={{ position: 'relative' }}>
                                    <label><FaUserTie /> Secondary Coordinators (Max 19)</label>
                                    <div style={{ display: 'flex', alignItems: 'center', border: '2px solid var(--srm-border)', borderRadius: '8px', background: 'white' }}>
                                        <FaSearch style={{ marginLeft: '10px', color: '#999' }} />
                                        <input type="text" className="form-control" style={{ border: 'none', background: 'transparent' }}
                                            value={editCoordinatorSearch}
                                            onChange={(e) => { setEditCoordinatorSearch(e.target.value); setShowEditCoordinatorDropdown(true); }}
                                            onFocus={() => setShowEditCoordinatorDropdown(true)}
                                            placeholder="Search staff to add as secondary coordinator..."
                                            disabled={editCoordinators.length >= 19} />
                                    </div>
                                    {showEditCoordinatorDropdown && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid var(--srm-border)', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto', zIndex: 999, marginTop: '5px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
                                            {filteredEditCoordinators.length === 0 ? (
                                                <div style={{ padding: '15px', textAlign: 'center', color: '#666' }}>No staff found</div>
                                            ) : (
                                                filteredEditCoordinators.map(s => (
                                                    <div key={s._id} onClick={() => addEditCoordinator(s)} style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid #eee' }}>
                                                        <strong>{s.name}</strong> <span style={{ color: '#666', fontSize: '12px', marginLeft: '10px' }}>{s.email}</span>
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    )}
                                    {editCoordinators.length > 0 && (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '8px' }}>
                                            {editCoordinators.map(c => (
                                                <span key={c._id} style={{ background: '#17a2b8', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    👤 {c.name}
                                                    <span onClick={() => removeEditCoordinator(c._id)} style={{ cursor: 'pointer', fontWeight: 'bold' }}>×</span>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                    <button type="submit" className="btn btn-success" disabled={editSaving}>{editSaving ? <><Spinner /> Saving...</> : 'Save Changes'}</button>
                                    <button type="button" className="btn btn-danger" onClick={() => setShowEditEvent(false)}>Cancel</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {!showAttendance && activeSection === 'create' && (
                    <CreateEvent
                        onSuccess={() => { fetchEvents(); setActiveSection('events'); }}
                        draftToEdit={draftToEdit}
                        onDraftsChanged={refreshDrafts}
                    />
                )}

                {!showAttendance && activeSection === 'drafts' && (
                    <div className="card">
                        <h3 className="card-title"><FaFileAlt /> Draft Events ({drafts.length})</h3>
                        {drafts.length === 0 ? (
                            <p style={{ color: '#666' }}>No drafts saved. Use "Save Draft" while creating an event to come back to it later.</p>
                        ) : (
                            drafts.map(draft => (
                                <div key={draft.id} className="event-card">
                                    <div>
                                        <h4>{draft.formData.name || '(untitled draft)'}</h4>
                                        {draft.formData.synopsis && <p style={{ fontSize: '13px', color: '#666' }}>{draft.formData.synopsis}</p>}
                                        <p style={{ fontSize: '12px', color: '#999' }}>
                                            {draft.formData.date ? `📅 ${new Date(draft.formData.date).toLocaleDateString()}` : 'No date set'}
                                            {draft.formData.time && ` at ${draft.formData.time}`}
                                            {draft.formData.venue && ` 📍 ${draft.formData.venue}`}
                                        </p>
                                        <p style={{ fontSize: '12px', color: '#999' }}>
                                            Students selected: {draft.selectedStudents?.length || 0} · Saved {new Date(draft.savedAt).toLocaleString()}
                                        </p>
                                    </div>
                                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                        <span className="badge badge-warning">DRAFT</span>
                                        <button className="btn btn-sm btn-success" onClick={() => handleResumeDraft(draft)}>
                                            <FaPlayCircle /> Resume Editing
                                        </button>
                                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteDraft(draft.id)}>
                                            <FaTrash /> Delete
                                        </button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                )}

                {!showAttendance && activeSection === 'events' && (
                <div className="card">
                    <h3 className="card-title">My Events</h3>
                    {events.length === 0 ? (
                        <p style={{ color: '#666' }}>No events created yet.</p>
                    ) : (
                        events.map(event => (
                            <div key={event._id} className="event-card">
                                <div>
                                    <h4>{event.name}</h4>
                                    <p style={{ fontSize: '13px', color: '#666' }}>{event.synopsis}</p>
                                    <p style={{ fontSize: '12px', color: '#999' }}>
                                        📅 {new Date(event.date).toLocaleDateString()} at {event.time}
                                        {event.venue && ` 📍 ${event.venue}`}
                                    </p>
                                    <p style={{ fontSize: '12px', color: '#999' }}>
                                        Students: {event.students?.length || 0}
                                    </p>
                                    {event.coordinators?.length > 0 && (
                                        <p style={{ fontSize: '12px', color: '#666' }}>
                                            👥 Coordinators:&nbsp;
                                            {event.coordinators.map((c, index) => (
                                                <span key={c._id}>
                                                    {c._id === event.createdBy?._id ? (
                                                        <span style={{ color: '#0a1e3c', fontWeight: 'bold' }}>⭐ {c.name} (Primary)</span>
                                                    ) : (
                                                        <span style={{ color: '#17a2b8' }}>👤 {c.name}</span>
                                                    )}
                                                    {index < event.coordinators.length - 1 && ', '}
                                                </span>
                                            ))}
                                        </p>
                                    )}
                                    {event.poster && (
                                        <div style={{ marginTop: '5px' }}>
                                            <img
                                                src={`${getStaticBaseUrl()}/${event.poster}`}
                                                alt="Event Poster" 
                                                style={{ maxWidth: '100px', maxHeight: '100px', borderRadius: '8px', cursor: 'pointer' }}
                                                onClick={() => handleViewPoster(event.poster)}
                                            />
                                        </div>
                                    )}
                                </div>
                                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                                    <span className={`badge ${event.status === 'upcoming' ? 'badge-success' : event.status === 'completed' ? 'badge-danger' : 'badge-warning'}`}>
                                        {event.status}
                                    </span>
                                    <button className="btn btn-sm btn-primary" onClick={() => handleViewEvent(event._id)} disabled={busyEventId === event._id}>
                                        {busyEventId === event._id ? <Spinner /> : <FaEye />} View Students
                                    </button>
                                    {event.createdBy?._id === user?._id && event.status !== 'completed' && (
                                        <button className="btn btn-sm btn-warning" onClick={() => openEditEvent(event)} disabled={busyEventId === event._id}>
                                            {busyEventId === event._id ? <Spinner /> : <FaEdit />} Edit
                                        </button>
                                    )}
                                    {event.status !== 'completed' &&
                                        (event.createdBy?._id === user?._id || event.coordinators?.some(c => c._id === user?._id)) && (
                                        <>
                                            {hasEventStarted(event) ? (
                                                <button className="btn btn-sm btn-success" onClick={() => handleMarkAttendance(event)}>
                                                    <FaCheckCircle /> Mark Attendance
                                                </button>
                                            ) : (
                                                <span
                                                    className="badge badge-warning"
                                                    title="Attendance can be marked once the event starts"
                                                    style={{ display: 'flex', alignItems: 'center', padding: '6px 10px' }}
                                                >
                                                    Starts {formatIST(getEventStartDateTime(event))} IST
                                                </span>
                                            )}
                                            <button className="btn btn-sm btn-warning" onClick={() => openInviteModal(event)}>
                                                <FaLink /> Generate Invite
                                            </button>
                                        </>
                                    )}
                                    {event.status === 'completed' && event.reportPath && (
                                        <a
                                            className="btn btn-sm"
                                            style={{ background: '#6c757d', color: 'white', textDecoration: 'none' }}
                                            href={`${getStaticBaseUrl()}/${event.reportPath}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <FaFilePdf /> Download Report
                                        </a>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
                )}
                </div>
            </div>
        </div>
    );
};

export default StaffDashboard;